import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormMode } from 'src/app/controls/common/form-mode';
import { IBoxAccessibility } from 'src/app/models/interfaces/box-accessibility.interface';
import { IGroup } from 'src/app/models/interfaces/group.interface';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { IViewAccessibility } from 'src/app/models/interfaces/view-accessibility.interface';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { ParametersValuesFormComponent } from '../../parameters/parameters-values-form/parameters-values-form.component';
import { GroupChildrenFormComponent } from '../group-children-form/group-children-form.component';
import { GroupDetailContext } from '../group-detail-context';
import { GroupFormComponent } from '../group-form/group-form.component';
import { GroupViewsAccessibilityComponent } from '../group-views-accessibility/group-views-accessibility.component';
import { UserChildrenFormComponent } from '../user-children-form/user-children-form.component';

@Component({
    selector: 'app-group-detail',
    templateUrl: './group-detail.component.html',
    styleUrls: ['./group-detail.component.scss'],
    imports: [MatTabsModule, GroupFormComponent, GroupChildrenFormComponent, UserChildrenFormComponent, ParametersValuesFormComponent, GroupViewsAccessibilityComponent],
})
export class GroupDetailComponent implements OnInit {
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Reference au composant groupe d'onglets.
     */
    @ViewChild(MatTabGroup)
    tabGroup: MatTabGroup;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public groupUpdated: EventEmitter<IGroup> = new EventEmitter();

    @Output()
    public paramsValuesUpdated: EventEmitter<IParamsValues> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public accessibilitiesUpdated: EventEmitter<IBoxAccessibility[]> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public viewsAccessibilitiesUpdated: EventEmitter<IViewAccessibility[]> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Contexte du formulaire.
     */
    public _context: GroupDetailContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<GroupDetailContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: GroupDetailContext) {
        // set the latest value for _data BehaviorSubject
        this._context$.next(value);
    }

    constructor(private paramsValuesService: ParamsValuesService) {}

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        /**
         * S'abonne aux changements de contexte
         */
        this._context$.subscribe((x) => {
            this._context = x;
        });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont sauvegardés.
     * @param user: Infos du group modifié.
     */
    public onGroupUpdated(group: IGroup) {
        this.groupUpdated.emit(group);
    }

    public onParamsValuesUpdated(paramsvalues: IParamsValues) {
        this.paramsValuesUpdated.emit(paramsvalues);
    }

    public onAccessibilitiesUpdated(boxesAccessibility: IBoxAccessibility[]) {
        this.accessibilitiesUpdated.emit(boxesAccessibility);
    }

    public onViewsAccessibilitiesUpdated(viewsAccessibility: IViewAccessibility[]) {
        this.viewsAccessibilitiesUpdated.emit(viewsAccessibility);
    }

    public onParamsExported() {
        this.paramsValuesService
            .exportForGroup(this._context.editedGroupId)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Paramètres du groupe d'utilisateurs ${this._context.editedGroupId}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont annulés.
     * @param user: Infos de l'article modifié.
     */
    public onCanceled(value: boolean) {
        this.canceled.emit(value);
    }
}
